import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import './TagModal.css';

const TagModal = ({
  isOpen,
  onClose,
  subscriptionId,
  fanId,
  currentTag,
  onTagUpdate,
}) => {
  const [availableTags, setAvailableTags] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [error, setError] = useState(null);

  const fetchTags = useCallback(async () => {
    if (!subscriptionId) return;
    setIsLoading(true);
    setError(null);

    try {
      const jwt = Cookies.get('user_token');
      const response = await axios.post(
        'https://unbot.io/api/v1.0/proxy',
        {
          method: 'GET',
          path: 'v2.0/tags/',
          parameters: {},
          subscriptionId: subscriptionId,
        },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );

      if (response.data?.result && Array.isArray(response.data.result)) {
        setAvailableTags(response.data.result);
      } else if (Array.isArray(response.data)) {
        setAvailableTags(response.data);
      } else {
        setAvailableTags([]);
      }
    } catch (err) {
      console.error('Error fetching tags:', err);
      setError('Impossible de charger les étiquettes');
      setAvailableTags([]);
    } finally {
      setIsLoading(false);
    }
  }, [subscriptionId]);

  useEffect(() => {
    if (isOpen) {
      fetchTags();
      setShowRemoveConfirm(false);
    }
  }, [isOpen, fetchTags]);

  const handleAddTag = async (tag) => {
    if (isUpdating || !fanId) return;
    setIsUpdating(true);
    setError(null);

    try {
      const jwt = Cookies.get('user_token');
      await axios.post(
        'https://unbot.io/api/v1.0/proxy',
        {
          method: 'POST',
          path: 'v2.0/tags/user',
          parameters: {
            fanId: fanId,
            tagId: tag._id,
          },
          subscriptionId: subscriptionId,
        },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );

      if (onTagUpdate) {
        onTagUpdate(tag);
      }
      onClose();
    } catch (err) {
      console.error('Error adding tag:', err);
      setError("Erreur lors de l'ajout de l'étiquette");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRemoveTag = async () => {
    if (isUpdating || !currentTag || !fanId) return;
    setIsUpdating(true);
    setError(null);

    try {
      const jwt = Cookies.get('user_token');
      // Extraire l'ID du fan en retirant le préfixe "users/"
      const fanUserId = fanId.replace('users/', '');

      await axios.post(
        'https://unbot.io/api/v1.0/proxy',
        {
          method: 'DELETE',
          path: `v2.0/tags/user/${fanUserId}`,
          parameters: {},
          subscriptionId: subscriptionId,
        },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );

      if (onTagUpdate) {
        onTagUpdate(null);
      }
      onClose();
    } catch (err) {
      console.error('Error removing tag:', err);
      setError("Erreur lors de la suppression de l'étiquette");
    } finally {
      setIsUpdating(false);
      setShowRemoveConfirm(false);
    }
  };

  const handleTagClick = (tag) => {
    // Si le tag cliqué est déjà assigné, proposer la suppression
    if (currentTag && currentTag._id === tag._id) {
      setShowRemoveConfirm(true);
      return;
    }
    // Sinon, ajouter le tag (remplacera l'ancien si existant)
    handleAddTag(tag);
  };

  if (!isOpen) return null;

  return (
    <div className="tag-modal-overlay" onClick={onClose}>
      <div className="tag-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="tag-modal-header">
          <h3>🏷️ Étiquettes</h3>
          <button className="tag-modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        {currentTag && (
          <div className="tag-modal-current">
            <span className="tag-modal-current-label">Étiquette actuelle :</span>
            <div className="tag-modal-current-tag">
              <span
                className="tag-badge large"
                style={{
                  ...getTagBackgroundStyle(currentTag.color),
                  color: getContrastColor(getTagPrimaryColor(currentTag.color)),
                }}
              >
                {currentTag.name}
              </span>
              {!showRemoveConfirm ? (
                <button
                  className="tag-remove-btn"
                  onClick={() => setShowRemoveConfirm(true)}
                  disabled={isUpdating}
                  title="Retirer l'étiquette"
                >
                  <i className="fas fa-trash-alt"></i>
                </button>
              ) : (
                <div className="tag-remove-confirm">
                  <span>Retirer ?</span>
                  <button
                    className="tag-confirm-btn danger"
                    onClick={handleRemoveTag}
                    disabled={isUpdating}
                  >
                    {isUpdating ? '...' : 'Oui'}
                  </button>
                  <button
                    className="tag-confirm-btn"
                    onClick={() => setShowRemoveConfirm(false)}
                    disabled={isUpdating}
                  >
                    Non
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {error && <div className="tag-modal-error">{error}</div>}

        <div className="tag-modal-body">
          {isLoading ? (
            <div className="tag-modal-loading">
              <i className="fas fa-spinner fa-spin"></i>
              <span>Chargement...</span>
            </div>
          ) : availableTags.length === 0 ? (
            <div className="tag-modal-empty">
              <p>Aucune étiquette disponible.</p>
              <p className="tag-modal-hint">
                Créez des étiquettes depuis la plateforme Uncove pour les utiliser ici.
              </p>
            </div>
          ) : (
            <>
              <p className="tag-modal-subtitle">
                {currentTag ? 'Remplacer par :' : 'Sélectionner une étiquette :'}
              </p>
              <div className="tag-list">
                {availableTags.map((tag) => {
                  const isActive = currentTag && currentTag._id === tag._id;
                  return (
                    <button
                      key={tag._id}
                      className={`tag-list-item ${isActive ? 'active' : ''}`}
                      onClick={() => handleTagClick(tag)}
                      disabled={isUpdating}
                      style={{
                        '--tag-color': getTagPrimaryColor(tag.color),
                      }}
                    >
                      <span
                        className="tag-color-dot"
                        style={getTagBackgroundStyle(tag.color)}
                      />
                      <span className="tag-name">{tag.name}</span>
                      {isActive && <i className="fas fa-check tag-check"></i>}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper pour extraire la couleur de fond d'un tag (hex ou linear gradient)
function getTagBackgroundStyle(color) {
  if (!color) return { backgroundColor: '#6366f1' };
  
  if (color.type === 'hex' && typeof color.value === 'string') {
    return { backgroundColor: color.value };
  }
  
  if (color.type === 'linear' && color.value && Array.isArray(color.value.colors)) {
    const { inclination = 135, colors } = color.value;
    const gradientStops = colors
      .map(([position, hexColor]) => `${hexColor} ${position * 100}%`)
      .join(', ');
    return { background: `linear-gradient(${inclination}deg, ${gradientStops})` };
  }
  
  return { backgroundColor: '#6366f1' };
}

// Helper pour extraire la première couleur hex d'un tag (pour le contraste)
function getTagPrimaryColor(color) {
  if (!color) return '#6366f1';
  
  if (color.type === 'hex' && typeof color.value === 'string') {
    return color.value;
  }
  
  if (color.type === 'linear' && color.value && Array.isArray(color.value.colors)) {
    const colors = color.value.colors;
    const midIndex = Math.floor(colors.length / 2);
    return colors[midIndex]?.[1] || '#6366f1';
  }
  
  return '#6366f1';
}

// Helper pour le contraste de couleur
function getContrastColor(hexColor) {
  if (!hexColor) return '#ffffff';
  if (typeof hexColor !== 'string') return '#ffffff';
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#1a1a2e' : '#ffffff';
}

export default TagModal;
